/**
 * Customers.js — Customers tab
 * "Who is buying and how valuable are they?"
 * Pulls from /api/analytics/customers and /api/analytics/buyer-segments
 */
import React, { useState, useEffect, useCallback } from 'react';
import { get } from '../api';

const RANGES = [
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
];

const SEG_COLORS = {
  hot:  '#EF4444',
  warm: '#F59E0B',
  cool: '#3B82F6',
  cold: '#71717A',
  dead: '#3f3f46',
};

const TIER_COLORS = {
  core:      '#10B981',
  low_tier:  '#F59E0B',
  non_buyer: '#71717A',
};

const TIER_LABELS = {
  core:      'Core',
  low_tier:  'Low-Tier',
  non_buyer: 'Non-Buyer',
};

const SEG_ORDER = ['hot', 'warm', 'cool', 'cold', 'dead'];

const fmtDollar = v => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const fmtPct = v => v != null ? `${Number(v).toFixed(1)}%` : '—';
const fmtNum = v => v != null ? Number(v).toLocaleString() : '—';

const sectionLabel = {
  fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 14,
};

function KpiCard({ label, value, sub, valueColor }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 22px', minWidth: 0 }}>
      <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: valueColor || 'inherit' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Customers() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      get(`/api/analytics/customers?range=${range}`),
      get('/api/analytics/buyer-segments'),
    ])
      .then(([customers, tiers]) => {
        setData(customers);
        setTierData(tiers);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};
  const segments = data?.segment_breakdown || [];
  const buyerLevels = tierData?.buyer_levels || [];
  const crossTab = tierData?.cross_tab || [];

  // Helper: get count from buyer_levels array
  const tierCount = (level) => {
    const row = buyerLevels.find(r => r.buyer_level === level);
    return row ? Number(row.count) : 0;
  };

  // Build cross-tab matrix: { segment: { core, low_tier, non_buyer, total } }
  const crossTabMatrix = {};
  crossTab.forEach(row => {
    if (!crossTabMatrix[row.segment]) {
      crossTabMatrix[row.segment] = { core: 0, low_tier: 0, non_buyer: 0 };
    }
    crossTabMatrix[row.segment][row.buyer_level] = Number(row.count);
  });

  // Build sorted rows with totals, filter out zero-total rows
  const crossTabRows = SEG_ORDER
    .map(seg => {
      const d = crossTabMatrix[seg] || { core: 0, low_tier: 0, non_buyer: 0 };
      const total = d.core + d.low_tier + d.non_buyer;
      return { segment: seg, ...d, total };
    })
    .filter(r => r.total > 0);

  const crossTabTotals = crossTabRows.reduce(
    (acc, r) => ({ core: acc.core + r.core, low_tier: acc.low_tier + r.low_tier, non_buyer: acc.non_buyer + r.non_buyer, total: acc.total + r.total }),
    { core: 0, low_tier: 0, non_buyer: 0, total: 0 }
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 2 }}>Customers</h2>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Who is buying and how valuable are they?</div>
        </div>
        <div style={{ display: 'flex', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {RANGES.map(r => (
            <button key={r.value} onClick={() => setRange(r.value)} style={{
              padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
              border: 'none', cursor: 'pointer',
              background: range === r.value ? 'var(--blue)' : 'transparent',
              color: range === r.value ? '#fff' : 'var(--muted)',
            }}>{r.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Loading...</div>
      ) : (
        <>
          {/* Buyer Tiers Summary */}
          <div style={{ marginBottom: 20 }}>
            <div style={sectionLabel}>Buyer Tiers</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <KpiCard
                label="Core"
                value={fmtNum(tierCount('core'))}
                sub="Premium buyers"
                valueColor={TIER_COLORS.core}
              />
              <KpiCard
                label="Low-Tier"
                value={fmtNum(tierCount('low_tier'))}
                sub="Entry buyers"
                valueColor={TIER_COLORS.low_tier}
              />
              <KpiCard
                label="Non-Buyer"
                value={fmtNum(tierCount('non_buyer'))}
                sub="Not yet purchased"
                valueColor={TIER_COLORS.non_buyer}
              />
            </div>
          </div>

          {/* KPI Cards */}
          <div style={{ marginBottom: 8 }}>
            <div style={sectionLabel}>Purchase Metrics</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <KpiCard label="AOV" value={fmtDollar(kpis.aov)} sub="Avg order value" />
            <KpiCard label="Customer LTV" value={fmtDollar(kpis.ltv)} sub="All-time avg" />
            <KpiCard label="New Customers" value={fmtNum(kpis.new_customers)} sub={fmtPct(kpis.new_pct) + ' of buyers'} />
            <KpiCard label="Returning" value={fmtNum(kpis.returning_customers)} sub={fmtPct(kpis.returning_pct) + ' of buyers'} />
            <KpiCard label="Total Buyers" value={fmtNum(kpis.total_buyers)} sub={`in ${range.toUpperCase()}`} />
          </div>

          {/* Segment Performance Table */}
          {segments.length > 0 && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={sectionLabel}>Segment Performance</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Segment', 'Subscribers', 'Revenue', 'Rev/Sub'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {segments.map(s => (
                    <tr key={s.segment} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: SEG_COLORS[s.segment] || '#71717A', flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, textTransform: 'capitalize', color: SEG_COLORS[s.segment] || 'inherit' }}>{s.segment}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(s.subscriber_count)}</td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", color: s.revenue > 0 ? 'var(--green)' : 'var(--muted)' }}>{fmtDollar(s.revenue)}</td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--muted)' }}>
                        {s.rev_per_sub > 0 ? `$${Number(s.rev_per_sub).toFixed(4)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tier × Segment Cross-Tab */}
          {crossTabRows.length > 0 && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <div style={sectionLabel}>Tier Distribution by Segment</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Segment', 'Core', 'Low-Tier', 'Non-Buyer', 'Total'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {crossTabRows.map(row => (
                    <tr key={row.segment} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: SEG_COLORS[row.segment] || '#71717A', flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, textTransform: 'capitalize', color: SEG_COLORS[row.segment] || 'inherit' }}>{row.segment}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: TIER_COLORS.core }}>
                        {row.core > 0 ? fmtNum(row.core) : '—'}
                      </td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", color: TIER_COLORS.low_tier }}>
                        {row.low_tier > 0 ? fmtNum(row.low_tier) : '—'}
                      </td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", color: 'var(--muted)' }}>
                        {row.non_buyer > 0 ? fmtNum(row.non_buyer) : '—'}
                      </td>
                      <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        {fmtNum(row.total)}
                      </td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr style={{ borderTop: '2px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
                    <td style={{ padding: '12px 12px', fontWeight: 700, fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', color: 'var(--muted)' }}>Total</td>
                    <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: TIER_COLORS.core }}>{fmtNum(crossTabTotals.core)}</td>
                    <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: TIER_COLORS.low_tier }}>{fmtNum(crossTabTotals.low_tier)}</td>
                    <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: 'var(--muted)' }}>{fmtNum(crossTabTotals.non_buyer)}</td>
                    <td style={{ padding: '12px 12px', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{fmtNum(crossTabTotals.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
